'use client';

/**
 * VerticalStack — shared "tools that don't talk" recognition section.
 *
 * Used by all 4 verticals. Renders 4 stack-card tiles in an
 * `auto-fit / minmax(260px, 1fr)` grid. Each tile shows a stack-item
 * name, a brand line (line-through if `replaced` is set), and a
 * one-sentence pain.
 *
 * Per-vertical variation:
 *   - `accent` drives the numbered kicker color on each tile.
 *   - `sectionNum` defaults to '01' (events / b2b) or '02' (retail /
 *     services where the Gap section is 01).
 */

import React from 'react';
import SectionLabel from '../../home/SectionLabel';
import type { StackCopy, VerticalAccent } from './types';

type Props = {
  sectionNum: string;
  accent: VerticalAccent;
  copy: StackCopy;
};

const VerticalStack = ({ sectionNum, accent, copy }: Props) => {
  return (
    <section
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num={sectionNum} label={copy.label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {copy.headlineA} <span style={{ color: '#B4C1D8' }}>{copy.headlineB}</span>
        </h2>
        <p style={{ color: '#B4C1D8', fontSize: 18, maxWidth: 720, margin: '0 0 40px' }}>{copy.subhead}</p>

        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {copy.items.map((it, i) => (
            <div
              key={it.name}
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 16,
                padding: '22px 22px 24px',
              }}
            >
              <div className="mono" style={{ fontSize: 11, letterSpacing: 2, color: accent, marginBottom: 8 }}>
                0{i + 1}
              </div>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: '#F5F7FA',
                  marginBottom: 4,
                }}
              >
                {it.name}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 13,
                  color: '#6B7593',
                  marginBottom: 14,
                  textDecoration: it.replaced ? 'line-through' : 'none',
                  textDecorationColor: it.replaced ? 'rgba(255, 77, 77, 0.5)' : undefined,
                }}
              >
                {it.brand}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: '#B4C1D8' }}>{it.pain}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VerticalStack;
